import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Typography, CrossIcon } from '../../index';
import { useFocusableElements } from '../../hooks/useFocusableElements';

enum EDrawerPaddingXSize {
	small = 'px-1.5',
	large = 'px-5',
	none = '',
}

enum EDrawerPaddingTopSize {
	small = 'pt-1.5',
	large = 'pt-6 md:pt-8',
	none = '',
}

type PaddingXSize = keyof typeof EDrawerPaddingXSize;

type PaddingYSize = keyof typeof EDrawerPaddingTopSize;

type DrawerVariant = 'horizontal' | 'vertical';

interface IDrawerProps {
	children: React.ReactNode;
	isOpen: boolean;
	paddingX: PaddingXSize;
	paddingY?: PaddingYSize;
	paddingBottom?: boolean;
	title?: string;
	variant?: DrawerVariant;
	insideOfModal?: boolean;
	setIsOpen: (bool: boolean) => void;
}

export const Drawer = ({
	children,
	isOpen,
	paddingX,
	paddingY = 'none',
	paddingBottom = true,
	title,
	variant = 'horizontal',
	insideOfModal = false,
	setIsOpen,
}: IDrawerProps) => {
	const [animate, setAnimate] = useState(true);
	const drawerRef = useRef<HTMLDivElement | null>(null);

	const { onKeyDown } = useFocusableElements(drawerRef, isOpen);

	useEffect(() => {
		if (isOpen) {
			setAnimate(true);
			if (!insideOfModal) document.body.classList.add('overflow-hidden');
		}
		return () => {
			if (!insideOfModal) document.body.classList.remove('overflow-hidden');
			setAnimate(true);
		};
	}, [isOpen]);

	const drawerClasses = {
		horizontal: `left-5 bottom-0 top-0 ${
			animate ? 'animate-popupSlideIn' : 'animate-popupSlideOut'
		} md:left-0 md:w-1/2 md:max-w-[720px] md:ml-auto`,
		vertical: `left-0 bottom-0 ${
			animate ? 'animate-popupVerticalSlideIn' : 'animate-popupVerticalSlideOut'
		} md:h-[calc(100%_-_124px)]`,
	};

	return (
		<>
			{isOpen &&
				createPortal(
					<>
						<div
							className={`fixed z-[3000] top-0 left-0 w-full h-full bg-black/[.36] ${
								animate ? 'animate-fadeIn' : 'animate-fadeOut'
							}`}
							onClick={(e) => {
								e.stopPropagation();
								setAnimate(false);
							}}
							onAnimationEnd={() => !animate && setIsOpen(false)}
						/>
						<div
							ref={drawerRef}
							aria-hidden={!isOpen}
							className={`fixed bg-white right-0 h-full overflow-scroll z-[4000] ${drawerClasses[variant]}`}
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => {
								if (e.key === 'Escape') {
									e.preventDefault();
									e.stopPropagation();
									setAnimate(false);
								} else {
									onKeyDown(e);
								}
							}}>
							<div className="flex justify-between px-5 py-3.5 border-solid border-t-0 border-l-0 border-r-0 border-b border-b-gray-700 sticky top-0 z-10 bg-white">
								<Typography component="h2" size="lg" color="title" variant="title">
									{title}
								</Typography>
								<button
									className="cursor-pointer border-none bg-transparent opacity-80 transition-opacity hover:opacity-100"
									title="Close"
									aria-label="Close"
									onClick={() => setAnimate(false)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											setAnimate(false);
										}
									}}>
									<CrossIcon fill="#121212" />
								</button>
							</div>
							<div
								className={`grid ${paddingBottom ? 'pb-6 md:pb-8' : ''} ${
									EDrawerPaddingXSize[paddingX]
								} ${EDrawerPaddingTopSize[paddingY]}`}>
								{children}
							</div>
						</div>
					</>,
					document.getElementById('portal') as HTMLElement,
				)}
		</>
	);
};
